// Food, coffee and apres stop insertion. Runs AFTER the itinerary engine as a
// separable post-process (plan section 7.4 step 3): it places each enabled
// stop at a route node within its time window where a matching, open place is
// reachable, shifts the rest of the day later by the stop duration, and
// RE-VALIDATES against lift closes and the day's end. A stop that would break
// the safety guarantee is dropped with an explicit warning rather than
// stranding the skier. Time for stops is reserved up front by the caller
// (planning against a reduced end time), so the shifted day lands back on the
// real finish.

import type { ItineraryLeg, ItineraryStopType } from "./itinerary";
import type { PlannerPreferences } from "./preferences";
import { venueOpeningDuring, type GoogleOpeningHours } from "./venue-hours";
import { assessedVenueOpening, venueVisitWarnings, type PlaceSourceEvidence } from "./venue-policy";
import { MAX_ROUTE_WALK_MINUTES, MAX_WALK_SHARE } from "./route-quality";
export type { GoogleOpeningHours, GooglePeriodPoint } from "./venue-hours";
import {
  APRES_DURATION_MIN,
  APRES_TARGET_BEFORE_END_H,
  COFFEE_AM_DEFAULT_HOUR,
  COFFEE_DURATION_MIN,
  COFFEE_PM_DEFAULT_HOUR,
  LUNCH_DURATION_MIN,
  STOP_PLACEMENT_WINDOW_H,
} from "./stop-constants";

type MovementLeg = Extract<ItineraryLeg, { kind: "lift" | "trail" | "link" | "bus" }>;

export type StopPlace = PlaceSourceEvidence & {
  googlePlaceId: string;
  nodeId: string | null;
  category: string; // mountain_restaurant | restaurant | cafe | bar | apres
  name: string | null;
  priceLevel: number | null; // 0-4
  rating: number | null;
  cuisine: string | null;
  mapsUri: string | null;
  reservable: boolean | null;
  lat: number;
  lng: number;
  openingHours: GoogleOpeningHours;
};

const REVALIDATE_EPS = 1e-6;

export const PRICE_CEILING: Record<PlannerPreferences["lunch"]["price"], number> = {
  budget: 1,
  mid: 2,
  treat: 4,
};

/**
 * How long a stop reserves, in minutes. The single source of truth shared by
 * stop placement here and the PlanEditor's whole-visit opening check.
 */
export function stopDurationMinutes(
  stopType: ItineraryStopType,
  lunchStyle: PlannerPreferences["lunch"]["style"],
): number {
  if (stopType === "coffee") return COFFEE_DURATION_MIN;
  if (stopType === "apres") return APRES_DURATION_MIN;
  return lunchStyle === "skip" ? 0 : LUNCH_DURATION_MIN[lunchStyle];
}

/**
 * The venue categories a stop will match, derived from the preferences (lunch
 * style, apres style). Shared by stop placement and the editor's "change venue"
 * picker so the picker can never offer an off-style venue. Empty = no venue
 * needed (a packed lunch).
 */
type StopCategoryPreferences = Pick<PlannerPreferences, "lunch" | "apres">;

export function stopCategories(
  stopType: ItineraryStopType,
  prefs: StopCategoryPreferences,
): string[] {
  if (stopType === "coffee") return ["cafe", "mountain_restaurant"];
  if (stopType === "lunch") {
    if (prefs.lunch.style === "packed" || prefs.lunch.style === "skip") return [];
    return prefs.lunch.style === "cheap"
      ? ["mountain_restaurant", "cafe", "restaurant"]
      : ["restaurant", "mountain_restaurant"];
  }
  return prefs.apres === "coffee-cake" ? ["cafe"] : prefs.apres === "quiet" ? ["bar"] : ["bar", "apres"];
}

type PlannedStop = {
  stopType: ItineraryStopType;
  targetHour: number;
  durationH: number;
  categories: string[]; // empty = no venue needed (packed lunch)
  priceCeiling: number;
};

/**
 * Per-stop target-time overrides set by the editor's retime controls. Each key
 * identifies one planned stop; morning and afternoon coffee are distinct so
 * they can be moved independently. Absent keys fall back to the default time.
 */
export type StopTimeKey = "lunch" | "coffeeAm" | "coffeePm" | "apres";
export type StopTimeOverrides = Partial<Record<StopTimeKey, number>>;

function parseHm(hm: string): number {
  const [h, m] = hm.split(":").map(Number);
  return (h || 0) + (m || 0) / 60;
}

/** The stops the preferences ask for, in chronological order. */
export function plannedStops(
  prefs: PlannerPreferences,
  endHour: number,
  overrides?: StopTimeOverrides,
): PlannedStop[] {
  const stops: PlannedStop[] = [];

  if (prefs.lunch.style !== "skip") {
    stops.push({
      stopType: "lunch",
      targetHour: overrides?.lunch ?? parseHm(prefs.lunch.time),
      durationH: stopDurationMinutes("lunch", prefs.lunch.style) / 60,
      categories: stopCategories("lunch", prefs),
      priceCeiling: PRICE_CEILING[prefs.lunch.price],
    });
  }

  const coffeeSlots: { key: "coffeeAm" | "coffeePm"; def: number }[] = [];
  if (prefs.coffeeStops === "morning" || prefs.coffeeStops === "both")
    coffeeSlots.push({ key: "coffeeAm", def: COFFEE_AM_DEFAULT_HOUR });
  if (prefs.coffeeStops === "afternoon" || prefs.coffeeStops === "both")
    coffeeSlots.push({ key: "coffeePm", def: COFFEE_PM_DEFAULT_HOUR });
  for (const slot of coffeeSlots) {
    stops.push({
      stopType: "coffee",
      targetHour: overrides?.[slot.key] ?? slot.def,
      durationH: stopDurationMinutes("coffee", prefs.lunch.style) / 60,
      categories: stopCategories("coffee", prefs),
      priceCeiling: 4,
    });
  }

  if (prefs.apres !== "skip") {
    stops.push({
      stopType: "apres",
      targetHour: overrides?.apres ?? endHour - APRES_TARGET_BEFORE_END_H,
      durationH: stopDurationMinutes("apres", prefs.lunch.style) / 60,
      categories: stopCategories("apres", prefs),
      priceCeiling: 4,
    });
  }

  return stops.sort((a, b) => a.targetHour - b.targetHour);
}

/** Total reserved stop time in hours, used by the caller to shrink the plan window. */
export function totalStopHours(prefs: PlannerPreferences, endHour: number): number {
  return plannedStops(prefs, endHour).reduce((s, st) => s + st.durationH, 0);
}

export function isOpenAt(hours: GoogleOpeningHours, weekday: number, decimalHour: number): boolean {
  return venueOpeningDuring(hours, weekday, decimalHour) !== "closed";
}

function matchScore(place: StopPlace, stop: PlannedStop): number {
  if (stop.categories.length > 0 && !stop.categories.includes(place.category)) return -1;
  if (place.priceLevel != null && place.priceLevel > stop.priceCeiling) return -1;
  // Prefer the listed category order, then rating.
  const catRank = stop.categories.indexOf(place.category);
  const catBonus = catRank >= 0 ? (stop.categories.length - catRank) * 0.3 : 0;
  return (place.rating ?? 3.5) + catBonus;
}

type Candidate = {
  index: number; // insert after legs[index]
  nodeId: string;
  atHour: number;
  place: StopPlace | null;
  score: number;
};

function rankedCandidates(
  legs: ItineraryLeg[],
  stop: PlannedStop,
  placesByNode: Map<string, StopPlace[]>,
  weekday: number,
  preferredNodeId?: string,
): Candidate[] {
  const candidates: Candidate[] = [];
  for (let i = 0; i < legs.length; i++) {
    const leg = legs[i];
    if (leg.kind === "stop") continue;
    const atHour = leg.arriveHour;
    if (Math.abs(atHour - stop.targetHour) > STOP_PLACEMENT_WINDOW_H) continue;
    const nodeId = leg.toNode;
    if (!nodeId) continue;
    const preferredBonus = preferredNodeId === nodeId ? 100 : 0;

    if (stop.categories.length === 0) {
      // Packed lunch: no venue needed, just the break closest to target.
      const score = 1 - Math.abs(atHour - stop.targetHour) + preferredBonus;
      candidates.push({ index: i, nodeId, atHour, place: null, score });
      continue;
    }

    for (const place of placesByNode.get(nodeId) ?? []) {
      const accessHours = (place.accessMinutes ?? 0) / 60;
      const opening = assessedVenueOpening(place, weekday, atHour + accessHours / 2, atHour + accessHours / 2 + stop.durationH);
      if (opening === "closed") continue;
      const ms = matchScore(place, stop);
      if (ms < 0) continue;
      // Closer to target time breaks ties between comparable places.
      const score = ms - Math.abs(atHour - stop.targetHour) * 0.5 + preferredBonus + (opening === "open" ? 0.5 : 0);
      candidates.push({ index: i, nodeId, atHour, place, score });
    }
  }
  return candidates.sort((a, b) => b.score - a.score);
}

function stopLabel(stop: PlannedStop, place: StopPlace | null): string {
  if (stop.stopType === "lunch" && stop.categories.length === 0) return "Packed lunch on the piste";
  if (!place) {
    return stop.stopType === "coffee" ? "Coffee stop" : stop.stopType === "apres" ? "Apres" : "Lunch";
  }
  const verb =
    stop.stopType === "coffee" ? "Coffee at" : stop.stopType === "apres" ? "Apres at" : "Lunch at";
  return `${verb} ${place.name ?? "a mountain spot"}`;
}

function makeStopLeg(stop: PlannedStop, cand: Candidate): ItineraryLeg {
  return {
    kind: "stop",
    stopType: stop.stopType,
    placeId: cand.place?.googlePlaceId ?? null,
    nodeId: cand.nodeId,
    departHour: cand.atHour,
    arriveHour: cand.atHour + stop.durationH + (cand.place?.accessMinutes ?? 0) / 60,
    label: stopLabel(stop, cand.place),
    place: cand.place
      ? {
          name: cand.place.name,
          category: cand.place.category,
          priceLevel: cand.place.priceLevel,
          rating: cand.place.rating,
          cuisine: cand.place.cuisine,
          mapsUri: cand.place.mapsUri,
          reservable: cand.place.reservable,
          lat: cand.place.lat,
          lng: cand.place.lng,
          source: cand.place.source,
          sourceUrl: cand.place.sourceUrl,
          sourceCheckedAt: cand.place.sourceCheckedAt,
          sourceUpdatedAt: cand.place.sourceUpdatedAt,
          accessDistanceM: cand.place.accessDistanceM,
          accessMinutes: cand.place.accessMinutes,
        }
      : undefined,
  };
}

/** Shift a leg's times by deltaH (returns a new leg). */
function shiftLeg(leg: ItineraryLeg, deltaH: number): ItineraryLeg {
  return { ...leg, departHour: leg.departHour + deltaH, arriveHour: leg.arriveHour + deltaH };
}

/** After shifting, do all lifts still depart before close and the day still end in time? */
function revalidate(
  legs: ItineraryLeg[],
  endHour: number,
  liftCloseHour: (refId: string) => number,
  placesByNode: Map<string, StopPlace[]>,
  weekday: number,
  legDeadlineHour?: (leg: MovementLeg) => number,
): boolean {
  let walkingMinutes = 0, movementMinutes = 0;
  for (const leg of legs) {
    if (leg.kind === "link") walkingMinutes += Math.max(0, (leg.arriveHour - leg.departHour) * 60);
    if (leg.kind === "stop") {
      walkingMinutes += leg.place?.accessMinutes ?? 0;
      movementMinutes += leg.place?.accessMinutes ?? 0;
      const place = (placesByNode.get(leg.nodeId) ?? []).find((place) => place.googlePlaceId === leg.placeId);
      if (place && assessedVenueOpening(place, weekday, leg.departHour + (place.accessMinutes ?? 0) / 120, leg.arriveHour - (place.accessMinutes ?? 0) / 120) === "closed") return false;
    } else movementMinutes += Math.max(0, (leg.arriveHour - leg.departHour) * 60);
    if (leg.kind !== "stop") {
      const deadline =
        legDeadlineHour?.(leg) ??
        (leg.kind === "lift" && leg.refId ? liftCloseHour(leg.refId) : endHour);
      if (leg.departHour > deadline + REVALIDATE_EPS) return false;
    }
    if (leg.arriveHour > endHour + REVALIDATE_EPS) return false;
  }
  return walkingMinutes <= MAX_ROUTE_WALK_MINUTES + REVALIDATE_EPS && walkingMinutes <= Math.max(5, movementMinutes * MAX_WALK_SHARE) + REVALIDATE_EPS;
}

export type InsertStopsArgs = {
  legs: ItineraryLeg[];
  prefs: PlannerPreferences;
  placesByNode: Map<string, StopPlace[]>;
  endHour: number;
  weekday: number;
  liftCloseHour: (refId: string) => number;
  /** Per-movement-leg deadline: lift close, bus last departure, daylight or day end. */
  legDeadlineHour?: (leg: MovementLeg) => number;
  /** Editor retime overrides; absent for a fresh plan. */
  stopTimeOverrides?: StopTimeOverrides;
  /** Stop nodes reached intentionally during route search; tried before the general scan. */
  preferredStopNodes?: Map<ItineraryStopType, string>;
};

export function insertStops(args: InsertStopsArgs): { legs: ItineraryLeg[]; warnings: string[] } {
  const { prefs, placesByNode, endHour, weekday, liftCloseHour, legDeadlineHour } = args;
  // No route, no stops: when the engine could not build a day, there is nothing
  // to anchor a lunch / coffee / apres break to. Returning early keeps the stop
  // pass silent rather than emitting a misleading "No lunch spot fits near your
  // route around 12:30" on a plan that does not exist.
  if (args.legs.length === 0) return { legs: [], warnings: [] };
  let legs = [...args.legs];
  const warnings: string[] = [];
  const stops = plannedStops(prefs, endHour, args.stopTimeOverrides);

  for (const stop of stops) {
    // Apres happens at the end of the day; anchor it to the REALISED finish
    // (a full day that hits its distance target finishes before the nominal
    // end), not the nominal end time, so it lands by the base.
    const effectiveStop =
      stop.stopType === "apres" && legs.length > 0
        ? { ...stop, targetHour: Math.min(stop.targetHour, legs[legs.length - 1].arriveHour - 0.25) }
        : stop;
    const candidates = rankedCandidates(
      legs,
      effectiveStop,
      placesByNode,
      weekday,
      args.preferredStopNodes?.get(stop.stopType),
    );
    if (!candidates.length) {
      if (stop.categories.length > 0) {
        const what =
          stop.stopType === "coffee" ? "coffee stop" : stop.stopType === "apres" ? "apres spot" : "lunch spot";
        warnings.push(`No ${what} fits near your route around ${formatHour(stop.targetHour)}.`);
      }
      continue;
    }

    // Try the next suitable venue if the favourite would miss a connection.
    // Recheck existing stops too: inserting an earlier stop can shift their visit.
    let accepted: { cand: Candidate; stopLeg: ItineraryLeg; candidatePlan: ItineraryLeg[] } | undefined;
    for (const cand of candidates) {
      const stopLeg = makeStopLeg(stop, cand);
      const before = legs.slice(0, cand.index + 1);
      const after = legs.slice(cand.index + 1).map((l) => shiftLeg(l, stop.durationH + (cand.place?.accessMinutes ?? 0) / 60));
      const candidatePlan = [...before, stopLeg, ...after];
      if (revalidate(candidatePlan, endHour, liftCloseHour, placesByNode, weekday, legDeadlineHour)) { accepted = { cand, stopLeg, candidatePlan }; break; }
    }

    if (accepted) {
      const { cand, stopLeg, candidatePlan } = accepted;
      legs = candidatePlan;
      if (cand.place?.source === "openstreetmap") {
        const opening = assessedVenueOpening(cand.place, weekday, stopLeg.departHour + (cand.place.accessMinutes ?? 0) / 120, stopLeg.arriveHour - (cand.place.accessMinutes ?? 0) / 120);
        if (stopLeg.kind === "stop" && stopLeg.place) stopLeg.place.openingState = opening;
        warnings.push(...venueVisitWarnings(cand.place, opening, stop.stopType));
      }
    } else {
      warnings.push(
        `We could not fit the ${stop.stopType === "apres" ? "apres" : stop.stopType} stop around opening times, walking limits and lift, bus or daylight windows. Continue with your planned route and check options at the base.`,
      );
    }
  }

  return { legs, warnings };
}

function formatHour(h: number): string {
  const hh = Math.floor(h);
  const mm = Math.round((h - hh) * 60);
  return `${String(hh).padStart(2, "0")}:${String(mm % 60).padStart(2, "0")}`;
}
